import { Suspense, type ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { BookOpen, Inbox, LogOut, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DelayedSpinner } from "@/components/DelayedSpinner";
import { GlobalLoadingBar } from "@/components/GlobalLoadingBar";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { cn } from "@/lib/utils";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors",
    isActive ? "bg-accent text-accent-foreground" : "text-mute hover:bg-accent/60 hover:text-ink-900",
  );

export function SupportDeskLayout({ children }: { children?: ReactNode }) {
  const { user, signOut } = useAuth();
  const brand = useBrand();
  const name = (user?.user_metadata?.full_name as string | undefined) || user?.email || "";

  return (
    <div className="app-gradient-bg flex h-[100dvh] w-full flex-col overflow-hidden">
      <header className="flex h-14 shrink-0 items-center gap-3 px-4">
        <img src={brand.logos.sidebar} alt={brand.shortName} className="h-10 w-auto max-w-[160px] object-contain" />
        <span className="hidden truncate text-sm font-semibold text-ink-900 sm:inline">Mesa de ayuda</span>
        <nav aria-label="Mesa de ayuda" className="flex items-center gap-1">
          <NavLink to="/support-desk" end className={navLinkClass}>
            <Inbox className="h-4 w-4" />
            <span className="hidden md:inline">Tickets</span>
          </NavLink>
          <NavLink to="/app/help/videos" className={navLinkClass}>
            <PlayCircle className="h-4 w-4" />
            <span className="hidden md:inline">Videos</span>
          </NavLink>
          <a
            href="/docs#introduction"
            target="_blank"
            rel="noopener noreferrer"
            className={navLinkClass({ isActive: false })}
          >
            <BookOpen className="h-4 w-4" />
            <span className="hidden md:inline">Documentación</span>
          </a>
        </nav>
        <div className="ml-auto flex min-w-0 items-center gap-2">
          <span className="hidden min-w-0 truncate text-xs text-mute sm:inline">{name}</span>
          <Button variant="ghost" size="sm" onClick={signOut} className="gap-1.5">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Cerrar sesión</span>
          </Button>
        </div>
      </header>
      <main className="app-panel relative mx-2 mb-2 flex flex-1 flex-col overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30">
          <GlobalLoadingBar />
        </div>
        <div className="m-2 flex min-h-0 flex-1 flex-col overflow-y-auto sm:m-3">
          <Suspense fallback={<DelayedSpinner />}>
            {children ?? <Outlet />}
          </Suspense>
        </div>
      </main>
    </div>
  );
}
